import React, {
  useEffect,
  useState,
} from 'react';
import { useRouter } from 'next/router';

import { ShareIcons } from '@/core/components/ShareIcons';
import { ThankYouWrapper } from '@/core/components/ThankYouWrapper';
import { MainMedia } from '@/core/components/MainMedia';
import { StoppedPageComponent } from '@/core/components/StoppedPageComponent';
import {
  ErrorsFields,
  FormState,
  SignupDetails,
  SignupFormPayload,
} from '@/core/interfaces/common';

import { SignupFormContainer } from '@/features/NewForm/SignupFormContainer';

import { request } from '@/utils/request';
import {
  handleCreateCustomFieldsParams,
  scrollToTop,
} from '@/utils/helpers';
import { googleAnalyticsPageview } from '@/utils/analytics';
import {
  getAnalyticsSessionId,
  trackFormSubmit,
} from '@/utils/beacon';

import {
  Description,
  EmailThankYouMessage,
  Footer,
  FooterLink,
  GeneralErrorMessage,
  Header,
  HeadlineDescription,
  LandingPageWrapper,
  Logo,
  LogoLink,
  LogoWrapper,
  ShareIconsWrapper,
  TextAreaDescription,
  TextDescription,
  Title,
  WantMoreWrapper,
  WhyTakeActionSection,
} from '@/themes/globalStyles';

import {
  Form,
  FormWrapper,
  StyledWhyTakeActionWrapper,
} from './SignupForm.styles';

export interface SubmitResponse {
  message?: string;
  status?: string;
}

export interface SignupFormPageProps {
  signupDetails: SignupDetails;
}

export const SignupForm = ({
  signupDetails,
}: SignupFormPageProps) => {
  const router = useRouter();
  const { uuid } = router.query;

  const {
    submitButtonText,
    facebookPixelEnabled,
    facebookPixelId,
    headlineConfig,
    isDonateButton,
    googleAnalyticsEnabled,
    googleAnalyticsId,
    signupPageImage,
    linkDonate,
    logo,
    optIn,
    status,
    organization,
    socialShareButtonEnabled,
    thankYouMessage,
    version,
  } = signupDetails;

  const {
    youtubeId,
    vimeoCode,
  } = headlineConfig;

  const [
    formState,
    setFormState,
  ] = useState<FormState>({
    customFields: [],
    email: '',
    firstName: '',
    isOptInEmail: true,
    isOptInText: true,
    lastName: '',
    phoneNumber: '',
    prefix: '',
    streetAddress: '',
    zipCode: '',
  });
  const [
    isActionTaken,
    setIsActionTaken,
  ] = useState(false);
  const [
    isPending,
    setIsPending,
  ] = useState(false);
  const [
    error,
    setError,
  ] = useState<ErrorsFields>({});

  useEffect(() => {
    const formCustomFields = signupDetails.customFields.map(customField => ({
      ...customField,
      value: '',
    }));

    setFormState(prevValues => ({
      ...prevValues,
      customFields: formCustomFields,
    }));
  }, [signupDetails]);

  useEffect(() => {
    if (facebookPixelId && facebookPixelEnabled) {
      import('react-facebook-pixel')
        .then(module => module.default)
        .then(ReactPixel => {
          ReactPixel.init(facebookPixelId, undefined, {
            autoConfig: true,
            debug: false,
          });
          ReactPixel.pageView(); // For tracking page view
        });
    }
  }, [
    facebookPixelId,
    facebookPixelEnabled,
  ]);

  useEffect(() => {
    if (googleAnalyticsId && googleAnalyticsEnabled) {
      googleAnalyticsPageview(googleAnalyticsId);
    }
  }, [
    googleAnalyticsId,
    googleAnalyticsEnabled,
  ]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setIsPending(true);
    const {
      customFields,
      ...restFormState
    } = formState;

    const params: SignupFormPayload = {
      ...restFormState,
      sessionId: getAnalyticsSessionId(),
      ...(customFields ?
        { customFields: handleCreateCustomFieldsParams(customFields) } :
        {}),
    };

    return request
      .post<SubmitResponse>(`/public/signup/${uuid}/`, params)
      .then(({ data }) => {
        if (data.status === 'error') {
          setError({
            submitError: data.message,
          });
        } else {
          trackFormSubmit({
            organization: organization.uuid,
            version,
          });
        }
        setIsActionTaken(true);

        return data;
      })
      .catch(({ response: { data = {} } = {} }) => {
        if (data?.status === 'error') {
          setError({
            generalError: data.message,
          });
        } else {
          setError(data);
        }
      })
      .finally(() => {
        setIsPending(false);
        scrollToTop();
      });
  };

  const renderActionTaken = () => (
    <ThankYouWrapper
      buttonColor={headlineConfig?.buttonColor || '#2979FF'}
      isDonateButton={isDonateButton}
      linkDonate={linkDonate}
      name={error?.submitError || 'Submission complete!'}
      socialShareButtonEnabled={socialShareButtonEnabled}
      textcolor={headlineConfig?.textColor || '#000'}
      uuid={uuid}
    >
      {!error?.submitError && thankYouMessage && (
        <EmailThankYouMessage
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: thankYouMessage,
          }}
        />
      )}
    </ThankYouWrapper>
  );

  if (status === 'stopped') {
    return (
      <StoppedPageComponent
        backgroundColor={headlineConfig?.backgroundColor || '#2979FF'}
        textColor={headlineConfig?.textColor || '#000'}
        title="This Signup has been stopped."
      />
    );
  }

  const websiteLink = organization.websiteUrl?.startsWith('http://') || organization.websiteUrl?.startsWith('https://') ? organization.websiteUrl : `https://${organization.websiteUrl}`;

  return (
    <LandingPageWrapper backgroundcolor={headlineConfig?.backgroundColor || '#2979FF'}>
      <Header
        backgroundcolor={headlineConfig?.backgroundColor || '#2979FF'}
        isActionTaken={isActionTaken}
        textcolor={headlineConfig?.textColor || '#000'}
      >
        <LogoWrapper>
          <LogoLink
            aria-label={organization.websiteUrl ? `${organization.name} website` : undefined}
            href={organization.websiteUrl ? websiteLink : undefined}
            target="_blank"
            rel="noopener noreferrer"
          >
            {logo ?
              (
                <Logo
                  src={logo}
                  alt=""
                  role="presentation"
                />
              ) :
              organization.name
            }
          </LogoLink>
        </LogoWrapper>
        {isActionTaken ?
          (
            renderActionTaken()
          ) :
          (
            <>
              <Title>
                {headlineConfig?.headlineText}
              </Title>
              {headlineConfig?.secondHeadlineText && (
                <Description>
                  {headlineConfig.secondHeadlineText}
                </Description>
              )}
            </>
          )}
      </Header>
      {!isActionTaken && (
        <>
          <FormWrapper version={version}>
            <WhyTakeActionSection>
              <StyledWhyTakeActionWrapper version={version}>
                <HeadlineDescription>
                  {headlineConfig?.descriptionHeadlineText}
                </HeadlineDescription>
                <MainMedia
                  vimeoCode={vimeoCode}
                  youtubeId={youtubeId}
                  mainImage={signupPageImage}
                />
                {headlineConfig?.descriptionBody ?
                  (
                    <TextDescription
                        // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: headlineConfig?.descriptionBody,
                      }}
                    />
                  ) :
                  (
                    <TextAreaDescription>{headlineConfig?.textArea}</TextAreaDescription>
                  )}
              </StyledWhyTakeActionWrapper>
            </WhyTakeActionSection>
            <Form version={version}>
              {error?.generalError && (
                <GeneralErrorMessage>{error?.generalError}</GeneralErrorMessage>
              )}
              <form
                autoComplete="off"
                onSubmit={handleSubmit}
              >
                <SignupFormContainer
                  backgroundColor={headlineConfig?.backgroundColor || '#2979FF'}
                  buttonColor={headlineConfig?.buttonColor || '#2979FF'}
                  error={error}
                  formState={formState}
                  isPending={isPending}
                  optIn={optIn}
                  setError={setError}
                  setFormState={setFormState}
                  submitButtonText={submitButtonText}
                  textColor={headlineConfig?.textColor || '#000'}
                />
              </form>
            </Form>
          </FormWrapper>
          <Footer
            backgroundcolor={headlineConfig?.backgroundColor || '#2979FF'}
            textcolor={headlineConfig?.textColor || '#000'}
          >
            {socialShareButtonEnabled && (
              <>
                <WantMoreWrapper>Share this page on your social media channels</WantMoreWrapper>
                <ShareIconsWrapper>
                  <ShareIcons
                    textcolor={headlineConfig?.textColor}
                    customIcons
                    uuid={uuid}
                  />
                </ShareIconsWrapper>
              </>
            )}
            <div>
              <FooterLink
                href={organization.websiteUrl ? websiteLink : undefined}
                target="_blank"
                rel="noopener noreferrer"
              >
                {organization.name}
              </FooterLink>
              &nbsp;
              |
              &nbsp;
              <FooterLink
                href="https://www.muster.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Muster&apos;s Privacy Policy
              </FooterLink>
              {organization.privacyPolicyUrl && (
                <>
                  &nbsp;
                  |
                  &nbsp;
                  <FooterLink
                    href={organization.privacyPolicyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {organization.name}
                    &apos;s Privacy Policy
                  </FooterLink>
                </>
              )}
            </div>
          </Footer>
        </>
      )}
    </LandingPageWrapper>
  );
};
